/* eslint-disable react/prop-types */
import React from 'react';
import {
  Stack,
  Text,
  Depths,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Icon,
  IconButton,
  TooltipHost,
} from '@fluentui/react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import { useId } from '@uifabric/react-hooks';
import moment from 'moment';
import Stepper from '../Stepper';
import * as sapi from '../../services/sapi';
import theme from '../../../../theme.style';
import useRestartIngestorMutation from '../../hooks/useRestartIngestorMutation';

export function getTotalIngestedQuantity(data) {
  const { stagedTransactionsBreakdown, nrbBreakdown, calcResultsBreakdown } = data;

  return (
    stagedTransactionsBreakdown.totalQuantity +
    nrbBreakdown.totalQuantity +
    calcResultsBreakdown.totalQuantity
  );
}

export function shouldShowContractReIngestionRecommendation(data) {
  return getTotalIngestedQuantity(data) === 0;
}

function useCreateTrackerMutation() {
  return useMutation('createContractTracker', async ({ contractId, month }) => {
    const { lastProcessedId } = await sapi.get(`/dri/last-processed-id/${month}`);

    return sapi.post('/trackers/contracts', {
      ThreadId: 1,
      StoreId: 2,
      Name: 'Store2TxByContractIngestion',
      ContractId: contractId,
      LastProcessedIdentifier: lastProcessedId,
      SnoozedTillTimestamp: moment.utc().subtract(5, 'days').toDate().toISOString(),
      PartitionName: '3p-tla',
      LastModifiedReason: `resetting tracker to ingest month ${month} using DRI toolkit..`,
      ProcessingFlag: 'I',
    });
  });
}

export default function ContractReIngestionRecommendation({ data, message }) {
  const { contractId, month } = data;

  const createTrackerMutation = useCreateTrackerMutation();
  const restartIngestorMutation = useRestartIngestorMutation();
  const tooltipId = useId();

  return (
    <Stack
      style={{
        display: 'inline-flex',
        backgroundColor: 'white',
        boxShadow: Depths.depth4,
        padding: 20,
      }}
      tokens={{ childrenGap: 14 }}
    >
      <Text style={{ fontWeight: 'bold' }}>Re-Ingest All Transactions for Contract</Text>

      <Text style={{ fontWeight: 'normal' }}>
        {message ||
          'There have been no transaction ingested for this contract for this month. To fix this we need to re-ingest all the transactions for this contract for this month using a contract tracker. Use the button below to create a contract tracker, then re-ingest transactions.'}
      </Text>

      {createTrackerMutation.error && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentError }}>
          {createTrackerMutation.error.response?.data?.message ||
            createTrackerMutation.error.message}
        </Text>
      )}

      {createTrackerMutation.isSuccess && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentTeal }}>
          <Icon iconName="StatusCircleCheckmark" />
          Successfully created a tracker that is needed to re-ingesting txns. Now you must re-ingest
          transactions using the button below.
        </Text>
      )}

      {restartIngestorMutation.error && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentError }}>
          {restartIngestorMutation.error.response?.data?.message ||
            restartIngestorMutation.error.message}
        </Text>
      )}

      {restartIngestorMutation.isSuccess && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentTeal }}>
          <Icon iconName="StatusCircleCheckmark" />
          Successfully restarted ingestor. Give it some time to send the transactions (~10 minutes),
          then re-submit the form above. If issues persist, then get help from an SME on the team.
        </Text>
      )}

      <Stack horizontal tokens={{ childrenGap: 14 }}>
        <Stepper number={1} disabled={createTrackerMutation.isSuccess}>
          <Stack horizontal tokens={{ childrenGap: 12 }}>
            <PrimaryButton
              onClick={() => createTrackerMutation.mutate({ contractId, month })}
              disabled={createTrackerMutation.isLoading || createTrackerMutation.isSuccess}
            >
              {createTrackerMutation.isLoading ? (
                <Spinner size={SpinnerSize.xSmall} />
              ) : (
                'Create/Update Tracker'
              )}
            </PrimaryButton>

            <TooltipHost
              content="Creates or updates the existing contract tracker to re-ingest all transactions for the chosen month. It finds an appropriate last processed id near the beginning of the chosen month, then sets the tracker's LastProcessedIdentifier."
              id={tooltipId}
              closeDelay={500}
            >
              <IconButton ariaLabel="Tracker Info" iconProps={{ iconName: 'Info' }} />
            </TooltipHost>
          </Stack>
        </Stepper>

        <Stepper number={2} disabled={!createTrackerMutation.isSuccess}>
          <PrimaryButton
            disabled={!createTrackerMutation.isSuccess || restartIngestorMutation.isLoading}
            onClick={() => restartIngestorMutation.mutate()}
          >
            {restartIngestorMutation.isLoading ? (
              <Spinner size={SpinnerSize.xSmall} />
            ) : (
              'Re-ingest'
            )}
          </PrimaryButton>
        </Stepper>
      </Stack>
    </Stack>
  );
}

ContractReIngestionRecommendation.propTypes = {
  data: PropTypes.shape({}).isRequired,
  message: PropTypes.string,
};

ContractReIngestionRecommendation.defaultProps = {
  message: null,
};
